import { useEffect, useState, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ProfileHeader from "./ProfileHeader";
import PersonalInfoForm from "./tabs/PersonalInfoForm";
import { editProfile, updatePasswordApi } from "../../../api/authAPI";
import { useAuth } from "../../../context/AuthProvider";
import { toast } from "react-toastify";
import SecurityForm from "./tabs/SecurityForm";
import ProfileTabs from "./ProfileTabs";
import Loader from "./components/Loader";

type EditMode = "profile" | "password" | null;

/* -----------------------------------------
   VALIDATION SCHEMAS
------------------------------------------ */

const personalInfoSchema = yup.object({
  firstName: yup.string().required().min(2),
  lastName: yup.string().required().min(2),
  gender: yup.string().required("Gender is required"),
  dob: yup.string().required("Date of birth is required"),
  // email: yup.string().email().required("Email is required"),
  email: yup
    .string()
    .email("Invalid email")
    .matches(
      /^[a-zA-Z0-9][a-zA-Z0-9._%+\-]*@[a-zA-Z0-9][a-zA-Z0-9.\-]*\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address",
    )
    .test(
      "no-spaces",
      "Email must not contain spaces",
      (v) => !/\s/.test(v || ""),
    )
    .test(
      "no-consecutive-dots",
      "Email cannot have consecutive dots",
      (v) => !/(\.\.)/.test(v || ""),
    )
    .required("Email is required"),

  phone: yup
    .string()
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
    .required("Phone is required"),
  pinCode: yup
    .string()
    .matches(/^[1-9]\d{5}$/, "Please enter a valid Pin Code")
    .required("Please enter a valid Pin Code")
    .nullable(),
  addressLine1: yup
    .string()
    .required("Address is required")
    .max(100, "Address cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9\s,.\-/#]+$/,
      "Only letters, numbers, spaces, and , . - / # are allowed",
    ),
  addressLine2: yup
    .string()
    .required("Address is required")
    .max(100, "Address cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9\s,.\-/#]+$/,
      "Only letters, numbers, spaces, and , . - / # are allowed",
    ),
});

const securitySchema = yup.object({
  currentPassword: yup
    .string()
    .required("Current password is required")
    .transform((value) => value?.trim()),

  newPassword: yup
    .string()
    .required("New password is required")
    .transform((value) => value?.trim())
    .test(
      "not-same-as-current",
      "New password cannot be same as current password",
      function (value) {
        const { currentPassword } = this.parent;
        if (!value || !currentPassword) return true;
        return value?.trim() !== currentPassword?.trim();
      },
    )
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter")
    .matches(/[a-z]/, "Must contain at least one lowercase letter")
    .matches(/\d/, "Must contain at least one number")
    .matches(/[@$!%*?&#]/, "Must contain at least one special character"),

  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

export default function ProfileContainer() {
  const { user, updateUser }: any = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>("profile");
  const [activeTab, setActiveTab] = useState<EditMode>("profile");
  const changed: Record<string, any> = {};

  useEffect(() => {
    if (activeTab === "password") {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [activeTab]);

  const activeSchema = useMemo(() => {
    return editMode === "password" ? securitySchema : personalInfoSchema;
  }, [editMode]);

  const methods = useForm({
    resolver: yupResolver(activeSchema as any),
    defaultValues: user || {},
    mode: "onTouched",
    shouldUnregister: false,
  });

  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (user) reset(user);
    setValue("phoneVerified", true);
  }, [user, reset]);

  const onError = (errors: any) => {
    const hasRequiredError = Object.values(errors).some((err: any) =>
      err?.message?.toLowerCase().includes("required"),
    );
    if (hasRequiredError) {
      toast.error("Fill all the mandatory fields");
    }
  };

  const handlePasswordUpdate = async (values: any) => {
    try {
      const res = await updatePasswordApi({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      if (res.data.status) {
        toast.success(res.data.message);

        reset({
          ...user,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        methods.clearErrors();

        setIsEditing(false);
      } else {
        // Backend errors (including last 3 password check)
        toast.error(res.data.message);

        methods.setError("backend", {
          type: "manual",
          message: res.data.message,
        });
      }
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const res = await editProfile(changed);
      if (!res.data.status) {
        toast.error(res.data.message);
        return;
      }
      updateUser(res.data.user);
      reset(res.data.user);
      setIsEditing(false);
      toast.success("Profile updated successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    if (!isEditing) return;
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 800));

    if (editMode === "password") {
      await handlePasswordUpdate(values);
    } else {
      Object.keys(values).forEach((key) => {
        if (values[key] !== user[key]) {
          changed[key] = values[key];
        }
      });
      if (Object.keys(changed).length === 0) {
        toast.info("No changes to update");
        setLoading(false);
        return;
      }
      await handleProfileUpdate();
    }
  };

  const handleCancel = () => {
    reset({
      ...user,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    methods.clearErrors();

    setValue("emailOriginal", user?.email);
    setValue("phoneOriginal", user?.phone);
    setValue("emailVerified", user?.emailVerified); // ⭐ ADD THIS
    setValue("phoneVerified", true);

    setIsEditing(false);
  };
  useEffect(() => {
    if (user) {
      setValue("emailOriginal", user.email);
      setValue("phoneOriginal", user.phone);
      setValue("emailVerified", user.emailVerified);
      setValue("phoneVerified", true);
    }
  }, [user]);

  const renderContent = () => {
    if (editMode === "password") {
      return <SecurityForm isEditing={isEditing} />;
    }

    return <PersonalInfoForm isEditing={isEditing} role={user?.role} />;
  };

  return (
    <>
      {loading && <Loader text="Updating, please wait..." />}

      <FormProvider {...methods}>
        <div className="min-h-screen  p-4 profile">
          <ProfileHeader
            user={user}
            onEdit={setIsEditing}
            setEditMode={setEditMode}
            activeTab={activeTab}
          />

          <div className="bg-white rounded-xl shadow mt-4">
            <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <div className={`p-6 ${isEditing ? undefined : "input-disabled"}`}>
              {renderContent()}
            </div>
            {isEditing && (
              <div className="flex justify-end gap-3 px-6 pb-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit, onError)}
                  disabled={loading}
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {editMode === "password" ? "Update Password" : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      </FormProvider>
    </>
  );
}
